<template>
  <Card cardClasses="shadow-sm border-0">
    <!-- Header Section -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-tags me-2"></i>Categories Management
      </h4>
      <div class="d-flex gap-3">
        <button class="btn btn-outline-secondary" @click="$router.go(-1)">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
        <button v-if="profile.role !== 'user'" class="btn btn-primary" @click="categoryModal = true; categoryMode = 'add'">
          <i class="bi bi-plus-circle me-1"></i> Add Category
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <!-- Loading Spinner -->
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading categories...</p>
      </div>

      <!-- Categories Content -->
      <div v-if="!loading" class="categories-content fade-in">
        <!-- Categories Count & Search -->
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center mb-2 mb-sm-0">
            <span class="badge bg-primary rounded-pill me-2">{{ categories.total }}</span>
            <span class="text-muted">Categories</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <UInput v-model="dataTableSearch" size="md" placeholder="Search category" class="mb-0 w-auto" />
            <button class="btn btn-primary" @click="onSearchClick">
              <i class="bi bi-search me-1"></i> Search
            </button>
            <button class="btn btn-outline-secondary" @click="onClearSearchClick" :disabled="!dataTableSearch || !dataTableSearch.length">
              <i class="bi bi-x-circle me-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Categories Grid -->
        <div class="row g-4">
          <div v-for="(category, index) in categories.data" :key="category.id" class="col-xxl-3 col-xl-4 col-md-6 col-sm-12">
            <div class="card category-card h-100 border-0 shadow-sm">
              <div class="card-body p-0">
                <div class="category-image-container">
                  <img class="category-image" :src="categoryImage(category.avatar)" alt="category">
                  <div class="category-overlay" :class="`bg-${['primary', 'success', 'info', 'warning'][index % 4]}-gradient`">
                    <h5 class="category-name">{{category.name}}</h5>
                  </div>
                </div>
                <div class="p-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="fw-medium">{{category.name}}</span>
                    <span class="badge bg-success">{{category.sale_margin_percentage}}% margin</span>
                  </div>
                  <p class="text-muted small mb-3" v-if="category.description">{{category.description}}</p>
                  <p class="text-muted small mb-3" v-else>No description available</p>
                  <div class="d-flex gap-2">
                    <button
                        class="btn btn-sm btn-outline-primary flex-grow-1"
                        @click="categoryModal = true; categoryMode = 'edit'; populateCategoryEdit(category)"
                    >
                      <i class="bi bi-pencil-square me-1"></i> Edit
                    </button>
                    <button
                        class="btn btn-sm btn-outline-success flex-grow-1"
                        @click="$router.push('/category/' + category.id + '/products')"
                    >
                      <i class="bi bi-box-seam me-1"></i> Products
                    </button>
                    <button
                        v-if="profile.role !== 'user'"
                        class="btn btn-sm btn-outline-danger flex-grow-1"
                        @click="confirmDeleteCategory(category)"
                    >
                      <i class="bi bi-trash me-1"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select class="form-select" v-model.number="dataTablePerPage" data="">
              <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination
              v-model="dataTablePage"
              :pages="totalPages"
              :threshold="2">
              <template #previous>Prev</template>
              <template #next>Next</template>
            </Pagination>
          </div>
        </div>

        <!-- Back Button -->
        <div class="d-flex justify-content-center mt-4">
          <button class="btn btn-outline-secondary" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Category Modal -->
    <Modal :open="categoryModal" @closed="categoryModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi" :class="categoryMode === 'add' ? 'bi-plus-circle' : 'bi-pencil-square'"></i>
          {{ categoryMode === 'add' ? 'Add New Category' : 'Edit Category' }}
        </h5>
        <button @click="categoryModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row g-4">
            <div class="col-md-4">
              <div class="text-center">
                <div class="category-image-preview mb-3">
                  <img class="img-fluid rounded" ref="categoryImage" :src="categoryImage(category.avatar)" alt="category">
                </div>
                <input
                    ref="refInputEl"
                    type="file"
                    name="avatar"
                    accept=".jpeg,.png,.jpg,.webp"
                    hidden
                    @change="displayImage"
                >
                <button
                    v-if="profile.role !== 'user' && profile.role !== 'admin'"
                    class="btn btn-outline-primary w-100"
                    @click="$refs.refInputEl.click()"
                >
                  <i class="bi bi-upload me-1"></i> Upload Image
                </button>
                <small class="d-block text-muted mt-2">
                  <i class="bi bi-info-circle me-1"></i>Supported: jpeg, jpg, png, webp. You'll crop it to {{ cropPreset.label }}
                </small>
              </div>
            </div>
            <div class="col-md-8">
              <div class="form-group mb-3">
                <label for="name" class="form-label fw-bold">
                  <i class="bi bi-tag me-1"></i>Category Name
                </label>
                <input
                  type="text"
                  id="name"
                  class="form-control"
                  placeholder="Enter category name"
                  v-model="category.name"
                >
              </div>
              <div class="form-group mb-3">
                <label for="description" class="form-label fw-bold">
                  <i class="bi bi-card-text me-1"></i>Description <span class="text-muted fst-italic small">(optional)</span>
                </label>
                <textarea
                  id="description"
                  class="form-control"
                  placeholder="Enter category description"
                  v-model="category.description"
                  rows="3"
                ></textarea>
              </div>
              <div class="form-group">
                <label for="sale_margin_percentage" class="form-label fw-bold">
                  <i class="bi bi-percent me-1"></i>Sale Margin Percentage
                </label>
                <input
                  type="number"
                  id="sale_margin_percentage"
                  class="form-control"
                  placeholder="Enter sale margin percentage"
                  v-model="category.sale_margin_percentage"
                  min="0"
                  step="0.01"
                >
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="categoryModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button type="button" class="btn btn-primary" v-if="categoryMode === 'add'" @click="addCategory">
          <i class="bi bi-plus-circle me-1"></i>Create Category
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateCategory(category.id)">
          <i class="bi bi-check-circle me-1"></i>Update Category
        </button>
      </div>
    </Modal>
    <!-- Add/Edit Category Modal End -->

    <ImageCropper
      :open="cropper.open"
      :file="cropper.file"
      :aspect-ratio="cropPreset.aspectRatio"
      :output-width="cropPreset.width"
      :output-height="cropPreset.height"
      title="Crop category image"
      @cropped="onImageCropped"
      @closed="cropper.open = false"
    />
  </Card>
</template>

<script>
import categoryService from "@/api/categoryService";
import formatDate from "../../utils/formatDate";
import {decrypt} from "@/utils/crypto";
import confirmDialog from "@/utils/confirmDialog";
import { CROP_PRESETS } from "@/utils/cropPresets";

export default {
  name: "Categories",

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      profile: {},
      loading: true,
      categories: {
        data: []
      },
      dataTableSearch: this.$route?.query?.search ?? '',
      dataTablePage: this.$route?.query?.page ? parseInt(this.$route.query.page) : 1,
      dataTablePerPage: this.$route?.query?.per_page ? parseInt(this.$route.query.per_page) : 10,
      categoryMode: 'add',
      categoryModal: false,
      category: {
        organization_id: this.organization.id,
        avatar: null,
        name: '',
        description: '',
        sale_margin_percentage: 0,
      },
      organization: {},
      croppedImage: null,
      cropper: { open: false, file: null },
      cropPreset: CROP_PRESETS.category,
      imageUrl: process.env.VUE_APP_API_IMAGE_URL + 'category/',
    }
  },

  methods: {
    formatDate,
    confirmDialog,
    getCategories: async function () {
      this.loading = true;
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage,
        search: this.dataTableSearch?.toString().trim() || undefined,
      }
      try {
        this.categories = await categoryService.getCategories(params);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        this.loading = false;
      }
    },
    onSearchClick(){
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getCategories()
    },
    onClearSearchClick(){
      if (!this.dataTableSearch) return;
      this.dataTableSearch = ''
      this.dataTablePage = 1
      this.updateRouteQuery()
      this.getCategories()
    },
    updateRouteQuery() {
      const query = {
        ...this.$route.query,
        page: this.dataTablePage !== 1 ? this.dataTablePage : undefined,
        per_page: this.dataTablePerPage !== 10 ? this.dataTablePerPage : undefined,
        search: this.dataTableSearch && this.dataTableSearch.trim().length ? this.dataTableSearch.trim() : undefined,
      }
      Object.keys(query).forEach(k => query[k] === undefined && delete query[k])
      this.$router.replace({ path: this.$route.path, query })
    },
    populateCategoryEdit(category) {
      this.croppedImage = null
      // Create a deep copy to avoid direct reference modification
      this.category = JSON.parse(JSON.stringify(category));
    },
    displayImage: function(event) {
      const { files } = event.target
      if (files && files.length) {
        if (files[0].size / 1024 / 1024 > 15) {
          alert("File size exceeds 15MB limit. Please choose a smaller file.");
          return;
        }
        this.cropper.file = files[0]
        this.cropper.open = true
      }
      event.target.value = ''
    },
    onImageCropped(dataUrl) {
      this.croppedImage = dataUrl
      this.$refs.categoryImage.src = dataUrl
    },
    async addCategory() {
      if (!this.category.name) {
        alert("Category name is required");
        return;
      }

      try {
        this.category.avatar = this.croppedImage || null
        await this.callCreateCategory()
      } catch (error) {
        console.error("Error adding category:", error);
      }
    },
    async updateCategory(id) {
      if (!this.category.name) {
        alert("Category name is required");
        return;
      }

      try {
        if (this.croppedImage) this.category.avatar = this.croppedImage
        await this.callUpdateCategory(id)
      } catch (error) {
        console.error("Error updating category:", error);
      }
    },
    async callCreateCategory() {
      try {
        let response = await categoryService.createCategory(this.category)
        if (response) {
          this.categoryModal = false
          await this.getCategories()
          this.resetCategoryForm()
        }
      } catch (error) {
        console.error("Error creating category:", error);
      }
    },
    async callUpdateCategory(id) {
      try {
        let response = await categoryService.updateCategory(this.category, id)
        if (response) {
          this.categoryModal = false
          await this.getCategories()
        }
      } catch (error) {
        console.error("Error updating category:", error);
      }
    },
    categoryImage(avatar) {
      if (!avatar) {
        return process.env.VUE_APP_API_PUBLIC_URL + 'images/placeholder.svg';
      }
      return avatar.startsWith('http') ? avatar : this.imageUrl + avatar;
    },
    resetCategoryForm() {
      // Reset the category form to default values
      this.category = {
        organization_id: this.organization.id,
        avatar: null,
        name: '',
        description: '',
        sale_margin_percentage: 0,
      };
      this.croppedImage = null;
    },
    confirmDeleteCategory(category) {
      confirmDialog(
        'Category',
        () => this.deleteCategory(category.id),
        `Are you sure you want to delete "${category.name}"? This action cannot be undone.`
      );
    },
    async deleteCategory(id) {
      try {
        const response = await categoryService.deleteCategory(id);
        if (response) {
          await this.getCategories();
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  },

  computed: {
    totalPages(){
      return this.categories?.last_page || 1
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.updateRouteQuery()
      this.getCategories()
    },
    dataTablePage(){
      this.updateRouteQuery()
      this.getCategories()
    },
    categoryModal: function () {
      if (!this.categoryModal && this.categoryMode === 'add') {
        this.resetCategoryForm();
      }
    },
  },

  created: async function () {
    try {
      this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
      this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
      await this.getCategories()
    } catch (error) {
      console.error("Error in created lifecycle hook:", error);
      this.loading = false;
    }
  },
}
</script>

<style scoped>
/* Categories page styles */
.categories-content {
  padding: 0.5rem;
}

/* Category card styles */
.category-card {
  transition: all 0.3s ease;
  overflow: hidden;
  border-radius: 12px;
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.category-image-container {
  position: relative;
  height: 180px;
  overflow: hidden;
}

.category-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.category-card:hover .category-image {
  transform: scale(1.05);
}

.category-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  transition: all 0.3s ease;
}

.category-name {
  color: white;
  margin: 0;
  font-weight: 600;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.5);
}

/* Background gradients for category overlays */
.bg-primary-gradient {
  background: linear-gradient(to top, rgba(67, 97, 238, 0.8), transparent);
}

.bg-success-gradient {
  background: linear-gradient(to top, rgba(16, 185, 129, 0.8), transparent);
}

.bg-info-gradient {
  background: linear-gradient(to top, rgba(59, 130, 246, 0.8), transparent);
}

.bg-warning-gradient {
  background: linear-gradient(to top, rgba(245, 158, 11, 0.8), transparent);
}

/* Modal styles */
.category-image-preview {
  height: 200px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.category-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .category-image-container {
    height: 150px;
  }
}
</style>
